import { EigenvalueDecomposition, Matrix } from 'ml-matrix';
import type { PlaneFit, Vec3 } from './types';
import { triCentroid } from './triangle';
import { normalize } from './vec';

/**
 * Smallest eigenvalue + its eigenvector of a symmetric 3×3 matrix
 *   [ a d e ]
 *   [ d b f ]
 *   [ e f c ]
 * via ml-matrix's eigendecomposition. For a least-squares plane this eigenvector
 * is the surface normal (least variance direction); the eigenvalue is the
 * variance along it (→ rms). The eigenvector's sign is mathematically arbitrary,
 * so it is canonicalized to make its dominant component positive (deterministic).
 */
export function smallestEigen3x3(
  a: number, b: number, c: number, d: number, e: number, f: number,
): { value: number; vector: Vec3 } {
  const evd = new EigenvalueDecomposition(
    new Matrix([
      [a, d, e],
      [d, b, f],
      [e, f, c],
    ]),
    { assumeSymmetric: true },
  );
  const values = evd.realEigenvalues;
  let mi = 0;
  for (let i = 1; i < values.length; i++) if (values[i] < values[mi]) mi = i;

  const col = evd.eigenvectorMatrix.getColumn(mi);
  let v: Vec3 = [col[0], col[1], col[2]];
  const ax = Math.abs(v[0]), ay = Math.abs(v[1]), az = Math.abs(v[2]);
  const dominant = ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
  if (v[dominant] < 0) v = [-v[0], -v[1], -v[2]];

  return { value: values[mi], vector: normalize(v) };
}

/** Least-squares plane through the given vertices (unit weights). */
export function fitPlaneToPoints(positions: ArrayLike<number>, vertexIndices: ArrayLike<number>): PlaneFit {
  const n = vertexIndices.length;
  if (n < 3) return { normal: [0, 0, 1], point: [0, 0, 0], rms: 0 };
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < n; i++) {
    const v = vertexIndices[i];
    cx += positions[v * 3];
    cy += positions[v * 3 + 1];
    cz += positions[v * 3 + 2];
  }
  cx /= n; cy /= n; cz /= n;
  let xx = 0, yy = 0, zz = 0, xy = 0, xz = 0, yz = 0;
  for (let i = 0; i < n; i++) {
    const v = vertexIndices[i];
    const dx = positions[v * 3] - cx, dy = positions[v * 3 + 1] - cy, dz = positions[v * 3 + 2] - cz;
    xx += dx * dx; yy += dy * dy; zz += dz * dz;
    xy += dx * dy; xz += dx * dz; yz += dy * dz;
  }
  xx /= n; yy /= n; zz /= n; xy /= n; xz /= n; yz /= n;
  const { value, vector } = smallestEigen3x3(xx, yy, zz, xy, xz, yz);
  return { normal: vector, point: [cx, cy, cz], rms: Math.sqrt(Math.max(0, value)) };
}

/**
 * Incremental plane accumulator (area-weighted through triangle centroids).
 * Lets region-grow refit the plane in O(1) as it accepts more triangles.
 */
export interface PlaneAccumulator {
  sw: number;
  sx: number; sy: number; sz: number;
  sxx: number; syy: number; szz: number; sxy: number; sxz: number; syz: number;
}

export const createPlaneAccumulator = (): PlaneAccumulator => ({
  sw: 0, sx: 0, sy: 0, sz: 0, sxx: 0, syy: 0, szz: 0, sxy: 0, sxz: 0, syz: 0,
});

export function accumulateTriangle(
  acc: PlaneAccumulator, positions: ArrayLike<number>, indices: ArrayLike<number>, tri: number, area: number,
): void {
  const [x, y, z] = triCentroid(positions, indices, tri);
  const w = area;
  acc.sw += w;
  acc.sx += w * x; acc.sy += w * y; acc.sz += w * z;
  acc.sxx += w * x * x; acc.syy += w * y * y; acc.szz += w * z * z;
  acc.sxy += w * x * y; acc.sxz += w * x * z; acc.syz += w * y * z;
}

export function fitFromAccumulator(acc: PlaneAccumulator): PlaneFit {
  if (acc.sw < 1e-12) return { normal: [0, 0, 1], point: [0, 0, 0], rms: 0 };
  const cx = acc.sx / acc.sw, cy = acc.sy / acc.sw, cz = acc.sz / acc.sw;
  const xx = acc.sxx / acc.sw - cx * cx;
  const yy = acc.syy / acc.sw - cy * cy;
  const zz = acc.szz / acc.sw - cz * cz;
  const xy = acc.sxy / acc.sw - cx * cy;
  const xz = acc.sxz / acc.sw - cx * cz;
  const yz = acc.syz / acc.sw - cy * cz;
  const { value, vector } = smallestEigen3x3(xx, yy, zz, xy, xz, yz);
  return { normal: vector, point: [cx, cy, cz], rms: Math.sqrt(Math.max(0, value)) };
}
